import { observable, action, computed } from "mobx";
import { clone, keys, trim } from "lodash";
import {
  ConsultantContactsSectionInput,
  ConsultantEducationSectionInput,
  ConsultantLanguageSkillsSectionInput,
  ConsultantPersonalSectionInput,
  ConsultantEditor_FormFragment,
  ConsultantWorkExperienceSectionInput,
  FormAddressInput,
  ConsultantEducationSectionItemInput,
  ConsultantWorkExperienceItemInput
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { store as rootStore } from "@store";
import { OkCancelDialogOptions } from "@containers";
import { parseSeSSN } from "@bonliva-crm/common";
import { ConsultantEditorStore } from "./ConsultantEditorStore";

export class ConsultantCVStore {
  @observable formKeysForApproval: string[] = [];
  @observable.shallow notApprovedEducations = [];
  @observable.shallow notApprovedWorkExperiences = [];

  constructor(private consultantId, private store: ConsultantEditorStore) {}

  importCV = (values?: ConsultantEditor_FormFragment) => {
    const lastNameCheck = () => {
      if (
        // DO NOT REMOVE Commented SSN check for future ref.
        // !!this.formSSN &&
        // !values.personal.ssn &&
        this.store.form.getValue("personal.lastName") &&
        values.personal.lastName.toUpperCase() !==
          (this.store.form.getValue("personal.lastName") as string).toUpperCase()
      ) {
        rootStore()
          .ui.confirmDialog.confirm(
            new OkCancelDialogOptions(
              locale["Surnames do not match. Do you want to continue anyway?"]
            )
          )
          .then((res) => {
            if (res === "ok") {
              updateForm();
            } else {
              // DO Nothing
            }
          });
      } else {
        updateForm();
      }
    };

    const updateForm = action(() => {
      this.updatePersonalAndAddress(values.personal, values.address);
      this.updatePersonalAboutAndDL(values.personal);
      this.updateContacts(values.contacts).then();
      this.updateWorkExperience(values.workExperience);
      this.updateEducation(values.education);
      this.updateLanguageSkills(values.languageSkills);
      this.updateExtraCurriculum(values.extraCurriculum);
      this.updateSystemString(values.knownSystems);
    });

    if (values) {
      if (
        !!this.formSSN &&
        !!values.personal.ssn &&
        this.formSSN !== parseSeSSN(values.personal.ssn)
      ) {
        rootStore()
          .ui.confirmDialog.confirm(
            new OkCancelDialogOptions(
              locale[
                "Social security numbers do not match. Do you want to continue anyway?"
              ]
            )
          )
          .then((res) => {
            if (res === "ok") {
              this.store.form.setValue("personal.ssn", null);
              updateForm();
            } else {
              // DO Nothing
            }
          });
      } else {
        lastNameCheck();
      }
    }
  };

  keyNotApproved = (key: string) => this.formKeysForApproval.includes(key);

  educationNotApproved = (item: ConsultantEducationSectionItemInput) =>
    this.notApprovedEducations.includes(item);

  workExperienceNotApproved = (item: ConsultantWorkExperienceItemInput) =>
    this.notApprovedWorkExperiences.includes(item);

  @computed get hasChangesInPersonalInfo() {
    return (
      this.keyNotApproved("personal.firstName") ||
      this.keyNotApproved("personal.lastName") ||
      this.keyNotApproved("personal.hasDrivingLicence") ||
      this.keyNotApproved("personal.about")
    );
  }

  @computed get hasChangesInContactInfo() {
    return (
      this.keyNotApproved("contacts.cellPhone") ||
      this.keyNotApproved("contacts.email") ||
      this.keyNotApproved("contacts.phone")
    );
  }

  @computed get hasChangesInAddressInfo() {
    return (
      this.keyNotApproved("address.city") ||
      this.keyNotApproved("address.country") ||
      this.keyNotApproved("address.streetName") ||
      this.keyNotApproved("address.zipCode")
    );
  }

  @computed get hasChangesInLanguagesSkillsInfo() {
    return !!this.formKeysForApproval.find((x) => x.includes("languageSkills"));
  }

  @computed get hasChangesInExtraCurriculum() {
    return !!this.formKeysForApproval.find((x) => x.includes("extraCurriculum"));
  }

  @computed get hasChangesInWorkExperienceInfo() {
    return this.notApprovedWorkExperiences.length > 0;
  }

  @computed get hasChangesInEducationInfo() {
    return this.notApprovedEducations.length > 0;
  }

  @computed get hasChangesInPersonalTab() {
    return (
      this.hasChangesInPersonalInfo ||
      this.hasChangesInAddressInfo ||
      this.hasChangesInContactInfo
    );
  }

  @computed get hasChangesInBackgroundTab() {
    return (
      this.hasChangesInWorkExperienceInfo ||
      this.hasChangesInEducationInfo ||
      this.hasChangesInLanguagesSkillsInfo ||
      this.hasChangesInExtraCurriculum
    );
  }

  @computed get hasChangesInExpertiseTab() {
    return this.formKeysForApproval.includes("expertise");
  }

  systemsHasChangedInProfession(professionStore: string) {
    return this.formKeysForApproval.includes(`${professionStore}.systems`);
  }

  get hasChangesInEconomyTab() {
    return false;
  }

  get hasChangesInMiscTab() {
    return false;
  }

  get hasChangeInReferencesTab() {
    return false;
  }

  @computed get validated() {
    return (
      !this.hasChangesInPersonalTab &&
      !this.hasChangesInBackgroundTab &&
      !this.hasChangesInExpertiseTab &&
      !this.hasChangesInExpertiseTab &&
      !this.hasChangesInMiscTab &&
      !this.hasChangeInReferencesTab
    );
  }

  @action approveKey = (key: string) => {
    this.formKeysForApproval = this.formKeysForApproval.filter((x) => x !== key);
  };

  @action approveKeyTemplate = (key: string) => {
    this.formKeysForApproval = this.formKeysForApproval.filter((x) => !x.includes(key));
  };

  @action approveEducation = (item: ConsultantEducationSectionItemInput) => {
    this.notApprovedEducations = this.notApprovedEducations.filter((x) => x != item);
  };

  @action approveWorkExperience = (item: ConsultantWorkExperienceItemInput) => {
    this.notApprovedWorkExperiences = this.notApprovedWorkExperiences.filter(
      (x) => x != item
    );
  };

  private get formSSN() {
    return this.store.form.getValue("personal.ssn");
  }

  private updateSystemString(knownSystem: string[]) {
    if (knownSystem.length <= 0) {
      return;
    }

    const { expertise } = this.store.form.values;
    if (!expertise) {
      return;
    }

    const selectedProfessions = keys(expertise)
      .filter((x) => expertise[x]?.professionName)
      .map((y) => ({
        profKey: y,
        professionName: expertise[y]?.professionName
      }));

    selectedProfessions.forEach((prof) => {
      const systems = this.store.getSystemsOnProfession(prof.professionName);
      const selectedSystems = systems.filter((x) => knownSystem.includes(x));
      const pendingSystems = knownSystem.filter((x) => !systems.includes(x));
      this.setArrayValueForApproval(`expertise.${prof.profKey}.systems`, selectedSystems);
      if (pendingSystems.length > 0) {
        rootStore().snackbars.info(
          `Skills ${pendingSystems.join(", ")} are not present in CRM for profession ${
            prof.professionName
          }`
        );
      }
    });
  }

  private updatePersonalAndAddress(
    personal: ConsultantPersonalSectionInput,
    address: FormAddressInput
  ) {
    if (!this.formSSN && !personal.ssn) {
      this.setSsn(personal);
      this.updatePersonal(personal);
      this.updateAddress(address);
    }

    if (!this.formSSN && !!personal.ssn) {
      this.setSsn(personal);
      this.store.ssnLookup("Sweden");
    }
    if (!!this.formSSN && this.formSSN === personal.ssn) {
      // Don't update personal info
    }
  }

  private setSsn = (personal: ConsultantPersonalSectionInput) => {
    const ssn = parseSeSSN(personal.ssn);
    ssn && this.store.form.setValue("personal.ssn", ssn);
  };

  private updatePersonal = (personal: ConsultantPersonalSectionInput) => {
    this.setSimpleValueForApproval("personal.firstName", trim(personal.firstName));
    this.setSimpleValueForApproval("personal.lastName", trim(personal.lastName));
  };

  private updatePersonalAboutAndDL(personal: ConsultantPersonalSectionInput) {
    this.setSimpleValueForApproval("personal.about", trim(personal.about));
    this.setSimpleValueForApproval(
      "personal.hasDrivingLicence",
      personal.hasDrivingLicence
    );
  }

  private updateContacts = async (contacts: ConsultantContactsSectionInput) => {
    const confirmUpdateContacts = async (
      previous: string,
      current: string,
      title: string,
      onConfirm: () => void
    ) => {
      const res = await rootStore().ui.confirmDialog.confirm(
        new OkCancelDialogOptions(title)
      );

      if (res === "ok") {
        onConfirm();
      }
    };

    if (contacts.cellPhone !== this.store.form.values.contacts.cellPhone) {
      const previous = this.store.form.values.contacts.cellPhone;
      const current = contacts.cellPhone;

      await confirmUpdateContacts(
        previous,
        current,
        locale[
          "Are you sure you want to update your mobile number with the new information?"
        ](previous, current),
        () => this.setSimpleValueForApproval("contacts.cellPhone", current)
      );
    }

    if (contacts.email !== this.store.form.values.contacts.email) {
      const prev = this.store.form.values.contacts.email;
      const current = contacts.email;

      await confirmUpdateContacts(
        prev,
        current,
        locale["Are you sure you want to update email with the new information?"](
          prev,
          current
        ),
        () => this.setSimpleValueForApproval("contacts.email", trim(current))
      );
    }

    if (contacts.phone !== this.store.form.values.contacts.phone) {
      const prev = this.store.form.values.contacts.phone;
      const current = contacts.phone;

      await confirmUpdateContacts(
        prev,
        current,
        locale[
          "Are you sure you want to update the phone number with the new information?"
        ](prev, current),
        () => this.setSimpleValueForApproval("contacts.phone", current)
      );
    }
  };

  private updateAddress = (address: FormAddressInput) => {
    this.setSimpleValueForApproval("address.city", trim(address.city));
    this.setSimpleValueForApproval("address.country", trim(address.country));
    this.setSimpleValueForApproval("address.streetName", trim(address.streetName));
    this.setSimpleValueForApproval("address.zipCode", trim(address.zipCode));
  };

  private updateExtraCurriculum = (extraCurriculum: string) => {
    this.setSimpleValueForApproval("extraCurriculum", trim(extraCurriculum));
  };

  private updateWorkExperience = (
    workExperience: ConsultantWorkExperienceSectionInput
  ) => {
    this.store.form.setValue("workExperience.items", workExperience.items);
    this.notApprovedWorkExperiences = Array.from(
      this.store.form.values.workExperience.items
    );
  };

  private updateEducation = (education: ConsultantEducationSectionInput) => {
    this.store.form.setValue("education.items", education.items);
    this.notApprovedEducations = Array.from(this.store.form.values.education.items);
  };

  private updateLanguageSkills = (
    languageSkills: ConsultantLanguageSkillsSectionInput
  ) => {
    this.setArrayValueForApproval("languageSkills.items", languageSkills.items);
  };

  @action private setSimpleValueForApproval = (key, value) => {
    if (!!value && value !== this.store.form.getValue(key)) {
      this.store.form.setValue(key, value);
      this.formKeysForApproval.push(key);
    }
  };

  @action private setArrayValueForApproval = (key, arr: any[]) => {
    if (!arr || arr.length === 0) {
      return;
    }

    this.store.form.setValue(key, clone(arr));

    arr.forEach((_, i) => {
      this.formKeysForApproval.push(`${key}.${i}`);
    });
  };
}
